import { ApiError, classify } from './errors'
import type {
  ApiEnvelope,
  Creative,
  DisclosureRecord,
  SearchParams,
  SearchResult,
  StateOption,
} from '../types'

/**
 * The archive API base URL, as a single build-time constant (docs/api.md §2).
 * It includes the `/public/disclosure/v1` path; every request path below is
 * relative to it.
 *
 *   Development  https://api-dev.spbx.app/public/disclosure/v1
 *   Production   https://api.spbx.app/public/disclosure/v1
 *
 * Falling back to the bare path keeps the same-origin option open, for a
 * CloudFront distribution that forwards /public/* to the API origin.
 */
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '/public/disclosure/v1').replace(
  /\/+$/,
  '',
)

/** Limits the API enforces; mirrored here so the UI never sends a request it knows will 400. */
export const SEARCH_LIMITS = {
  minTermLength: 2,
  maxTermLength: 120,
  maxPage: 200,
  maxPerPage: 100,
  defaultPerPage: 25,
} as const

/**
 * Responses carry `Cache-Control: public, max-age=300, must-revalidate`, and
 * corrections overwrite records in place with no version history. Holding a
 * response longer than this risks presenting a superseded legal record as current,
 * so nothing in the app may cache beyond it (docs/api.md §10).
 */
export const MAX_CACHE_MS = 300_000

type QueryValue = string | number | undefined

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = new URL(`${API_BASE_URL}${path}`, window.location.origin)

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === '') continue
    url.searchParams.set(key, String(value))
  }

  return url.toString()
}

/** The API always returns the platform envelope; on error `data` is a plain string. */
async function readEnvelope<T>(response: Response): Promise<ApiEnvelope<T> | null> {
  try {
    const raw = await response.text()
    if (!raw) return null
    return JSON.parse(raw) as ApiEnvelope<T>
  } catch {
    return null
  }
}

async function request<T>(
  path: string,
  query?: Record<string, QueryValue>,
  signal?: AbortSignal,
): Promise<T> {
  let response: Response

  try {
    // No credentials, no Authorization header: the API is public, open-CORS and
    // does not allow credentials.
    response = await fetch(buildUrl(path, query), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'omit',
      signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError('network', 0, 'The public archive could not be reached.')
  }

  const envelope = await readEnvelope<T>(response)

  // Branch on the HTTP status, not on envelope.code: a validation failure is
  // HTTP 400 while code is 500, so reading code would misclassify every 400 and
  // 404 as a server fault.
  if (!response.ok) {
    const detail = typeof envelope?.data === 'string' ? envelope.data : null
    throw classify(response.status, detail)
  }

  if (!envelope || !('data' in envelope)) {
    throw new ApiError('network', response.status, 'The public archive returned an unreadable response.')
  }

  return envelope.data as T
}

/**
 * States that have a public archive. Configuration-driven and expected to change,
 * so it is never hardcoded. An empty array is valid and means the archive is off.
 */
export function getStates(signal?: AbortSignal): Promise<StateOption[]> {
  return request<StateOption[]>('/states', undefined, signal)
}

/** Search. `states` is required — figures are scoped per state. */
export function searchRecords(params: SearchParams, signal?: AbortSignal): Promise<SearchResult[]> {
  return request<SearchResult[]>(
    '/search',
    {
      states: params.states.join(','),
      purchaser: params.purchaser?.trim() || undefined,
      candidate: params.candidate?.trim() || undefined,
      from: params.from || undefined,
      to: params.to || undefined,
      page: params.page,
      per_page: params.per_page,
    },
    signal,
  )
}

/**
 * Record detail.
 *
 * `states` is passed through from the search the user arrived from, so the detail
 * page shows the same states the results row did. Only codes that came from
 * /states may be sent: if none of the codes are states we publish, the API
 * DISCARDS the filter and returns every state rather than none, which would
 * silently show a wider set than the user asked for (docs/api.md §7).
 */
export function getRecord(
  id: string,
  states?: string[],
  signal?: AbortSignal,
): Promise<DisclosureRecord> {
  return request<DisclosureRecord>(
    `/records/${encodeURIComponent(id)}`,
    { states: states?.length ? states.join(',') : undefined },
    signal,
  )
}

/**
 * Signed creative URL, valid ~15 minutes. Requested only when the user opens a
 * record: never prefetched for a result list, never cached, never in a sitemap.
 */
export function getCreative(id: string, signal?: AbortSignal): Promise<Creative> {
  return request<Creative>(`/records/${encodeURIComponent(id)}/creative`, undefined, signal)
}
